/**
 * FormWorkflowActionContext
 *
 * Carries the form's workflow actions — run workflow, change state, close &
 * create, export, email — plus `formId` and the shared `isCreating` flag, from
 * `FormWorkflowActionProvider` to the toolbar dropdown, the right-click menu and
 * the command palette.
 *
 * Invariant: never export a component here, and never import a project file for
 * its value — only `react` and `import type`. Either one makes this a
 * self-accepting react-refresh boundary, and a dev-time edit then rebinds the
 * provider to a second `createContext` instance while a mounted consumer still
 * holds the first, so the hook throws until a full reload (SYD-2499).
 * `FormWorkflowActionContext.test.ts` gates both halves.
 */
import { createContext, useContext } from 'react';
import type { useFormCloseAndCreate } from '../../use/form/useFormCloseAndCreate';
import type { useFormWorkflowRun } from '../../use/form/useFormWorkflowRun';
import type { useFormStateChange } from '../../use/form/useFormStateChange';
import type { useFormExport } from '../../use/form/useFormExport';
import type { useFormEmail } from '../../use/form/useFormEmail';

type CloseAndCreateFields = Omit<
  ReturnType<typeof useFormCloseAndCreate>,
  'closeAndCreateDrawer'
>;
type WorkflowRunFields = Omit<
  ReturnType<typeof useFormWorkflowRun>,
  'workflowParameterSidebar'
>;
type StateChangeFields = Omit<
  ReturnType<typeof useFormStateChange>,
  'stateChangeModal'
>;
type ExportFields = Omit<ReturnType<typeof useFormExport>, 'exportModal'>;
type EmailFields = Omit<ReturnType<typeof useFormEmail>, 'emailModal'>;

export type FormWorkflowActionContextValue = CloseAndCreateFields &
  WorkflowRunFields &
  StateChangeFields &
  ExportFields &
  EmailFields & {
    formId: string;
    isCreating: boolean;
    setIsCreating: (creating: boolean) => void;
  };

export const FormWorkflowActionContext = createContext<FormWorkflowActionContextValue | null>(null);

export function useFormWorkflowAction(): FormWorkflowActionContextValue {
  const value = useContext(FormWorkflowActionContext);
  if (value === null) {
    throw new Error('useFormWorkflowAction must be used within FormWorkflowActionProvider');
  }
  return value;
}
